import { inject, Injectable } from '@angular/core';
import { capitalize } from 'es-toolkit/compat';
import { forkJoin, Observable, of, take } from 'rxjs';

import { SegmentAnalysesTopicDto, SegmentAnalysisInsightsForRestaurantDto } from '@malou-io/package-dto';
import { CsvAsStringArrays } from '@malou-io/package-utils';

import { LocalStorage } from '#core/storage/local-storage';
import { LightRestaurant } from '#modules/restaurants/models/restaurant';
import { SemanticAnalysisTopTopicForCsv } from '#modules/statistics-e-reputation/models/aggregated-semantic-analysis-interface';
import { AbstractCsvService } from '#modules/statistics/components/download-insights-modal/models/csv-service.abstract';
import { AggregatedStatisticsStore } from '#modules/statistics/stores/aggregated-store/aggregated-statistics.store';
import { formatDate } from '#shared/helpers/index';
import { DatesAndPeriod } from '#shared/models/index';

interface Data {
    dates: DatesAndPeriod;
    restaurants: LightRestaurant[];
    semanticAnalysisData: SegmentAnalysisInsightsForRestaurantDto[] | undefined;
}

interface Options {
    restaurants: LightRestaurant[];
}

@Injectable()
export class AggregatedSemanticAnalysisTopTopicsCsvInsightsService extends AbstractCsvService<Data, Options> {
    readonly currentLang = LocalStorage.getLang();
    private readonly _aggregatedStatisticsStore = inject(AggregatedStatisticsStore);

    protected _getData$({ restaurants }: Options): Observable<Data> {
        return forkJoin({
            dates: of(this._aggregatedStatisticsStore.datesFilter()),
            restaurants: of(restaurants),
            semanticAnalysisData: of(this._aggregatedStatisticsStore.semanticAnalysisData()).pipe(take(1)),
        });
    }

    protected override _isDataValid(data: Data): boolean {
        return !!data;
    }

    protected override _getCsvHeaderRow(): string[] {
        return [
            'Date Start',
            'Date End',
            'Restaurant Name',
            'Positive Topic 1',
            'Positive Topic 2',
            'Positive Topic 3',
            'Positive Topic 4',
            'Positive Topic 5',
            'Positive Topic 6',
            'Negative Topic 1',
            'Negative Topic 2',
            'Negative Topic 3',
            'Negative Topic 4',
            'Negative Topic 5',
            'Negative Topic 6',
        ];
    }

    protected override _getCsvDataRows({ dates, semanticAnalysisData, restaurants }: Data): CsvAsStringArrays {
        return this._buildCsvDataForTopTopics(dates.startDate, dates.endDate, restaurants, semanticAnalysisData).map((topic) => [
            topic.startDate,
            topic.endDate,
            topic.restaurantName,
            topic.positive1,
            topic.positive2,
            topic.positive3,
            topic.positive4,
            topic.positive5,
            topic.positive6,
            topic.negative1,
            topic.negative2,
            topic.negative3,
            topic.negative4,
            topic.negative5,
            topic.negative6,
        ]);
    }

    private _buildCsvDataForTopTopics(
        startDate: Date | null,
        endDate: Date | null,
        restaurants: LightRestaurant[],
        semanticAnalysisData?: SegmentAnalysisInsightsForRestaurantDto[]
    ): SemanticAnalysisTopTopicForCsv[] {
        return (
            semanticAnalysisData?.map((semanticAnalysisForRestaurant) => {
                const associatedRestaurant = restaurants.find((restaurant) => restaurant.id === semanticAnalysisForRestaurant.restaurantId);
                return {
                    startDate: startDate ? formatDate(startDate, false) : '',
                    endDate: endDate ? formatDate(endDate, false) : '',
                    restaurantName:
                        associatedRestaurant?.internalName ?? associatedRestaurant?.name ?? semanticAnalysisForRestaurant.restaurantId,
                    positive1: this._prettyDisplay(semanticAnalysisForRestaurant.topTopics.positiveTopics[0]),
                    positive2: this._prettyDisplay(semanticAnalysisForRestaurant.topTopics.positiveTopics[1]),
                    positive3: this._prettyDisplay(semanticAnalysisForRestaurant.topTopics.positiveTopics[2]),
                    positive4: this._prettyDisplay(semanticAnalysisForRestaurant.topTopics.positiveTopics[3]),
                    positive5: this._prettyDisplay(semanticAnalysisForRestaurant.topTopics.positiveTopics[4]),
                    positive6: this._prettyDisplay(semanticAnalysisForRestaurant.topTopics.positiveTopics[5]),
                    negative1: this._prettyDisplay(semanticAnalysisForRestaurant.topTopics.negativeTopics[0]),
                    negative2: this._prettyDisplay(semanticAnalysisForRestaurant.topTopics.negativeTopics[1]),
                    negative3: this._prettyDisplay(semanticAnalysisForRestaurant.topTopics.negativeTopics[2]),
                    negative4: this._prettyDisplay(semanticAnalysisForRestaurant.topTopics.negativeTopics[3]),
                    negative5: this._prettyDisplay(semanticAnalysisForRestaurant.topTopics.negativeTopics[4]),
                    negative6: this._prettyDisplay(semanticAnalysisForRestaurant.topTopics.negativeTopics[5]),
                };
            }) ?? []
        );
    }

    private _prettyDisplay(topTopic: SegmentAnalysesTopicDto): string {
        return topTopic ? capitalize(topTopic.translations?.[this.currentLang] ?? topTopic.name) : '';
    }
}
